import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Spinner } from 'react-bootstrap';
import { FaDownload } from 'react-icons/fa';
import { Button } from '../../../components/EntityList';
import { useLabels } from '../../../hooks/useLabels';
import ImportIssues from './ImportIssues';
import { downloadErrorReport } from './importFile';

const BANNER_CLASSES = {
	ok: 'tw:border-ok tw:bg-ok-soft',
	partial: 'tw:border-warn tw:bg-warn-soft',
	failed: 'tw:border-danger tw:bg-danger-soft',
};

export default function StepResult({ result, table, onReviewAgain, onStartOver, validating, fileName }) {
	const { t } = useLabels();
	const failedRows = result.results.filter((r) => r.outcome === 'failed');
	const counters = [
		{ key: 'import.created', value: result.created },
		{ key: 'import.updated', value: result.updated },
		{ key: 'import.failed', value: result.failed },
		{ key: 'import.warnings', value: result.warningDetails.length },
	];

	const downloadErrors = () => {
		const cellsByRow = new Map(table.rows.map((row) => [row.rowNumber, row.cells]));
		downloadErrorReport(
			table.headers,
			failedRows.map((r) => ({ rowNumber: r.rowNumber, cells: cellsByRow.get(r.rowNumber) || {}, error: r.error })),
			`errores-${(fileName || 'importacion').replace(/\.[^.]+$/, '')}.xlsx`,
		);
	};

	return (
		<div className='tw:space-y-4'>
			<div className={classnames('tw:rounded-[7px] tw:border tw:px-4 tw:py-3 tw:text-[13.5px]', BANNER_CLASSES[result.status])}>
				<strong>{t(`import.result.${result.status}`)}</strong>
			</div>

			<div className='tw:grid tw:grid-cols-2 tw:gap-3 tw:sm:grid-cols-4'>
				{counters.map((counter) => (
					<div key={counter.key} className='tw:rounded-panel tw:border tw:border-border tw:bg-surface tw:px-4 tw:py-3'>
						<div className='tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.06em] tw:text-ink-3'>
							{t(counter.key)}
						</div>
						<div className='tw:font-mono tw:text-[24px] tw:font-semibold tw:text-ink-0'>{counter.value}</div>
					</div>
				))}
			</div>

			<ImportIssues
				errors={failedRows.map((r) => ({ rowNumber: r.rowNumber, message: r.error }))}
				warnings={result.warningDetails.map((w) => ({ rowNumber: w.rowNumber, message: w.warning }))}
			/>

			<div className='tw:flex tw:flex-wrap tw:gap-2'>
				{failedRows.length > 0 && (
					<Button icon={<FaDownload />} onClick={downloadErrors}>
						{t('import.downloadErrors')}
					</Button>
				)}
				<Button onClick={onReviewAgain} disabled={validating}>
					{validating && <Spinner as='span' size='sm' animation='border' className='tw:mr-1' />}
					{t('import.reviewAgain')}
				</Button>
				<Button variant='primary' onClick={onStartOver}>
					{t('import.startOver')}
				</Button>
			</div>
		</div>
	);
}

StepResult.propTypes = {
	result: PropTypes.object.isRequired,
	table: PropTypes.object.isRequired,
	onReviewAgain: PropTypes.func.isRequired,
	onStartOver: PropTypes.func.isRequired,
	validating: PropTypes.bool,
	fileName: PropTypes.string,
};
